import React, { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useCountUp } from "../hooks/useCountUp";
import { useLazyLoad } from "../hooks/useLazyLoad";
import { aboutApi, skillsApi, projectsApi, workHistoryApi, resumeApi } from "../services/api";
import { AdminWorkHistory } from "../types/admin";
import profilePhoto from "../images/foto-perfil_02.jpg";
import CvPreviewModal from "./CvPreviewModal";
import "../css/about.css";
import "../css/carousel.css";

const StatCard: React.FC<{ target: number; suffix?: string; label: string }> = ({
  target,
  suffix = "",
  label,
}) => {
  const [ref, display] = useCountUp(target, suffix);

  return (
    <div className="stat-card">
      <div className="stat-number" ref={ref}>
        {display}
      </div>
      <span className="stat-label">{label}</span>
    </div>
  );
};

const MONTH_NAMES_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const parseApiDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  const year = parseInt(parts[0]);
  const month = parts.length >= 2 ? parseInt(parts[1]) - 1 : 0;
  if (isNaN(year)) return null;
  return new Date(year, month);
};

const formatMonth = (dateStr: string, lang: string): string => {
  const date = parseApiDate(dateStr);
  if (!date) return dateStr;
  const months = lang === "pt" ? MONTH_NAMES_PT : MONTH_NAMES_EN;
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatDateRange = (
  startDate: string,
  endDate: string | null,
  lang: string
): string => {
  const start = formatMonth(startDate, lang);
  const end = endDate
    ? formatMonth(endDate, lang)
    : lang === "pt"
      ? "Presente"
      : "Present";
  return `${start} - ${end}`;
};

const calculateDurationFromDates = (
  startDate: string,
  endDate: string | null,
  lang: string
): string => {
  const start = parseApiDate(startDate);
  if (!start) return "";
  const end = endDate ? parseApiDate(endDate) : new Date();
  if (!end) return "";

  let totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (totalMonths < 0) totalMonths = 0;

  const years = Math.floor(totalMonths / 12);
  const remaining = totalMonths % 12;

  if (lang === "pt") {
    const p: string[] = [];
    if (years > 0) p.push(`${years} ano${years > 1 ? "s" : ""}`);
    if (remaining > 0)
      p.push(`${remaining} ${remaining > 1 ? "meses" : "mês"}`);
    return p.length > 0 ? p.join(" e ") : "< 1 mês";
  }
  const p: string[] = [];
  if (years > 0) p.push(`${years} year${years > 1 ? "s" : ""}`);
  if (remaining > 0)
    p.push(`${remaining} month${remaining > 1 ? "s" : ""}`);
  return p.length > 0 ? p.join(", ") : "< 1 month";
};

const calculateTotalExperience = (
  items: AdminWorkHistory[],
  lang: string
): string => {
  if (items.length === 0) return "";

  const earliestStart = items.reduce<Date | null>((acc, item) => {
    const d = parseApiDate(item.startDate);
    if (!d) return acc;
    return !acc || d < acc ? d : acc;
  }, null);

  if (!earliestStart) return "";

  const now = new Date();
  let totalMonths =
    (now.getFullYear() - earliestStart.getFullYear()) * 12 +
    (now.getMonth() - earliestStart.getMonth());
  if (totalMonths < 0) totalMonths = 0;

  const years = Math.floor(totalMonths / 12);
  const remaining = totalMonths % 12;

  if (lang === "pt") {
    const p: string[] = [];
    if (years > 0) p.push(`${years} ano${years > 1 ? "s" : ""}`);
    if (remaining > 0)
      p.push(`${remaining} ${remaining > 1 ? "meses" : "mês"}`);
    return p.length > 0 ? p.join(" e ") : "< 1 mês";
  }
  const p: string[] = [];
  if (years > 0) p.push(`${years} year${years > 1 ? "s" : ""}`);
  if (remaining > 0)
    p.push(`${remaining} month${remaining > 1 ? "s" : ""}`);
  return p.length > 0 ? p.join(", ") : "< 1 month";
};



/* ── Component ────────────────────────────────────────────────── */

const About: React.FC = () => {
  const { lang, t } = useLanguage();
  const [sectionRef, isVisible] = useLazyLoad("300px");
  const [bio, setBio] = useState({ pt: "", en: "" });
  const [cvUrls, setCvUrls] = useState({ pt: "", en: "" });
  const [workHistory, setWorkHistory] = useState<AdminWorkHistory[]>([]);
  const [skillsCount, setSkillsCount] = useState(0);
  const [projectsCount, setProjectsCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [cvModalOpen, setCvModalOpen] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    const load = async () => {
      const [bioRes, whRes, skillsRes, projRes, resumeRes] = await Promise.all([
        aboutApi.getBio().catch(() => null),
        workHistoryApi.getAll({ pageSize: 100 }).catch(() => null),
        skillsApi.getAll({ pageSize: 100 }).catch(() => null),
        projectsApi.getAll({ pageSize: 100 }).catch(() => null),
        resumeApi.getInfo().catch(() => null),
      ]);

      if (bioRes?.success && bioRes.data) {
        setBio(bioRes.data);
      }
      if (whRes?.success && whRes.data) {
        setWorkHistory(whRes.data);
      }
      if (skillsRes?.success && skillsRes.data) {
        setSkillsCount(skillsRes.data.length);
      }
      if (projRes?.success && projRes.data) {
        setProjectsCount(projRes.data.length);
      }
      if (resumeRes?.success && resumeRes.data) {
        setCvUrls({
          pt: resumeRes.data.urlPtBr || "",
          en: resumeRes.data.urlEnUs || "",
        });
      }

      setLoaded(true);
    };

    void load();
  }, [isVisible]);

  const experienceText = useMemo(
    () => calculateTotalExperience(workHistory, lang),
    [workHistory, lang]
  );
  const bioHtml = lang === "pt" ? bio.pt : bio.en;

  return (
    <section
      className={`about lazy-section${loaded ? " lazy-section--visible" : ""}`}
      id="about"
      ref={sectionRef as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <h2 className="section-title">{t.aboutTitle}</h2>

        <div className="about-top">
          <div className="about-photo-container">
            <img
              src={profilePhoto}
              alt="Daniel Pereira Sanches"
              className="about-photo"
              loading="lazy"
            />
          </div>

          <div className="about-stats">
            <div className="stat-card">
              <div className="stat-number">{experienceText || "0"}</div>
              <span className="stat-label">{t.stats.experienceYears}</span>
            </div>
            <StatCard target={skillsCount} label={t.stats.technologies} />
            <StatCard
              target={projectsCount}
              suffix="+"
              label={t.stats.projects}
            />
          </div>
        </div>

        <div className="about-bio">
          <p
            className="about-bio-content"
            dangerouslySetInnerHTML={{ __html: bioHtml || "" }}
          />
        </div>

        {workHistory.length > 0 && (
          <div className="about-timeline">
            <div className="timeline-total-experience">
              <i className="fa-solid fa-briefcase" />
              <span>
                {lang === "pt" ? "Experiência total: " : "Total experience: "}
                <strong>{calculateTotalExperience(workHistory, lang)}</strong>
              </span>
            </div>
            {workHistory.map((item) => {
              const role =
                lang === "pt" ? item.rolePt : item.roleEn;
              const description =
                lang === "pt" ? item.descriptionPt : item.descriptionEn;
              const dateText = formatDateRange(
                item.startDate,
                item.endDate,
                lang
              );
              const duration = calculateDurationFromDates(
                item.startDate,
                item.endDate,
                lang
              );

              return (
                <div className="timeline-item" key={item.id}>
                  <h3 className="timeline-company">{item.company}</h3>
                  <span className="timeline-date">
                    {dateText}
                    {duration && (
                      <span className="timeline-duration">
                        <i className="fa-solid fa-clock" />
                        {duration}
                      </span>
                    )}
                  </span>
                  <p className="timeline-description">
                    {role && <strong>{role} — </strong>}
                    {description}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {(lang === "pt" ? cvUrls.pt : cvUrls.en) && (
          <div className="about-cv">
            <button
              type="button"
              onClick={() => setCvModalOpen(true)}
              className="btn-primary cv-download-link"
            >
              <i className="fa-solid fa-download" />
              <span>{t.cvButton}</span>
            </button>
          </div>
        )}

        <CvPreviewModal
          open={cvModalOpen}
          onClose={() => setCvModalOpen(false)}
          language={lang}
          downloadUrl={lang === "pt" ? cvUrls.pt : cvUrls.en}
        />
      </div>
    </section>
  );
};

export default About;
